var _excluded = ["closeMenuOnSelect", "components", "defaultValue", "disabled", "error", "errorMessage", "fieldSize", "fieldWidth", "id", "intensity", "isClearable", "isMulti", "label", "labelledBy", "menuMaxVisible", "menuPlacement", "menuWidth", "message", "name", "options", "placeholder", "required", "tabIndex", "value"];
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var n = Object.getOwnPropertySymbols(e); for (r = 0; r < n.length; r++) o = n[r], -1 === t.indexOf(o) && {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (-1 !== e.indexOf(n)) continue; t[n] = r[n]; } return t; }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React, { useRef } from 'react';
import CreatableSelect from 'react-select/creatable';
import styled from 'styled-components';
import FieldError from "../../FieldError/FieldError.js";
import FieldLabel from "../../FieldLabel/FieldLabel.js";
import FieldMessage from "../../FieldMessage/FieldMessage.js";
import ClearIndicator from "../Components/ClearIndicator.js";
import CreatableLabel from "../Components/CreatableLabel.js";
import CreatableMultiValueInput from "../Components/CreatableMultiValueInput.js";
import DropdownIndicator from "../Components/DropdownIndicator.js";
import GroupHeading from "../Components/GroupHeading.js";
import MultiValueRemove from "../Components/MultiValueRemove.js";
import { CLASS_NAME_PREFIX, IS_CREATABLE, MENU_POSITION, selectMenuSharedDefaults } from "../constants.js";
import { getCreatableNoOptionsMessage, getHasOptions, getPlaceholderText } from "../helpers.js";
import { getSharedStyles } from "../style.js";
var StyledCreatableSelect = styled(CreatableSelect).withConfig({
  componentId: "CL__sc-1yco75e-0"
})(["", ""], _ref => {
  var {
    error,
    fieldSize,
    fieldWidth,
    intensity,
    isMulti,
    isScrollable,
    menuMaxVisible,
    menuPlacement,
    menuWidth
  } = _ref;
  return getSharedStyles({
    error,
    fieldSize,
    fieldWidth,
    intensity,
    isMulti,
    isCreatable: IS_CREATABLE,
    isScrollable,
    menuMaxVisible,
    menuPlacement,
    menuWidth
  });
});
var getComponents = (hasInputValue, hasOptions, isMulti) => {
  var components = {
    ClearIndicator,
    GroupHeading,
    IndicatorSeparator: null,
    MultiValueRemove
  };

  // only show dropdown chevron indicator if there are options to show
  if (hasOptions) {
    components = _objectSpread(_objectSpread({}, components), {
      DropdownIndicator
    });
  } else {
    components = _objectSpread(_objectSpread({}, components), {
      DropdownIndicator: () => /*#__PURE__*/React.createElement(React.Fragment, null)
    });
  }

  /* if multi value, but text input has no values and there are no menu options,
  don't show menu until user starts to type */
  if (!hasInputValue && !hasOptions && isMulti) components = _objectSpread(_objectSpread({}, components), {
    Input: CreatableMultiValueInput,
    Menu: () => null
  });

  // multi value creatable selects get the type-as-pill custom input
  if (isMulti) {
    components = _objectSpread(_objectSpread({}, components), {
      Input: CreatableMultiValueInput
    });
  }
  return components;
};
var SelectMenuCreatable = /*#__PURE__*/React.forwardRef((_ref2, ref) => {
  var _options$length;
  var {
      closeMenuOnSelect = selectMenuSharedDefaults.closeMenuOnSelect,
      components,
      defaultValue,
      disabled = selectMenuSharedDefaults.disabled,
      error = selectMenuSharedDefaults.error,
      errorMessage = selectMenuSharedDefaults.errorMessage,
      fieldSize = selectMenuSharedDefaults.fieldSize,
      fieldWidth = selectMenuSharedDefaults.fieldWidth,
      id = selectMenuSharedDefaults.id,
      intensity = 'default',
      isClearable = selectMenuSharedDefaults.isClearable,
      isMulti = selectMenuSharedDefaults.isMulti,
      label,
      labelledBy,
      menuMaxVisible = selectMenuSharedDefaults.menuMaxVisible,
      menuPlacement,
      menuWidth = selectMenuSharedDefaults.menuWidth,
      message,
      name = selectMenuSharedDefaults.name,
      options,
      placeholder,
      required,
      tabIndex = selectMenuSharedDefaults.tabIndex,
      value
    } = _ref2,
    rest = _objectWithoutProperties(_ref2, _excluded);
  var hasOptions = getHasOptions(options);
  var inputValueRef = useRef('');
  var handleOnInputChange = newInputValue => {
    inputValueRef.current = newInputValue;
  };
  return /*#__PURE__*/React.createElement("div", rest['data-cy'] && {
    'data-cy': rest['data-cy']
  }, label && /*#__PURE__*/React.createElement(FieldLabel, {
    disabled: disabled,
    fieldSize: fieldSize,
    htmlFor: id,
    intensity: intensity,
    required: required
  }, label), /*#__PURE__*/React.createElement(StyledCreatableSelect, _extends({
    "aria-label": label,
    "aria-labelledby": labelledBy,
    classNamePrefix: CLASS_NAME_PREFIX,
    closeMenuOnSelect: isMulti ? false : closeMenuOnSelect,
    defaultValue: defaultValue,
    error: error,
    components: _objectSpread(_objectSpread({}, getComponents(!!inputValueRef, hasOptions, isMulti)), components),
    fieldSize: fieldSize,
    fieldWidth: fieldWidth,
    formatCreateLabel: CreatableLabel,
    hideSelectedOptions: true,
    id: "".concat(id, "-wrapper"),
    inputId: id,
    intensity: intensity,
    isClearable: isClearable,
    isDisabled: disabled,
    isMulti: isMulti,
    isScrollable: ((_options$length = options === null || options === void 0 ? void 0 : options.length) !== null && _options$length !== void 0 ? _options$length : 0) > menuMaxVisible,
    menuMaxVisible: menuMaxVisible,
    menuPlacement: menuPlacement,
    menuPosition: MENU_POSITION,
    menuWidth: menuWidth,
    name: name,
    noOptionsMessage: () => getCreatableNoOptionsMessage(inputValueRef.current, hasOptions, IS_CREATABLE),
    onInputChange: handleOnInputChange,
    options: options,
    placeholder: placeholder !== null && placeholder !== void 0 ? placeholder : getPlaceholderText(isMulti, hasOptions, IS_CREATABLE),
    required: required,
    tabIndex: tabIndex,
    tabSelectsValue: false,
    value: value
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    ,
    ref: ref
  }, rest)), message && !error && /*#__PURE__*/React.createElement(FieldMessage, {
    fieldSize: fieldSize,
    htmlFor: id
  }, message), errorMessage && error && /*#__PURE__*/React.createElement(FieldError, {
    fieldSize: fieldSize,
    htmlFor: id
  }, errorMessage));
});

/*
  ActionMeta is the second argument in react-select's onChange listener, in
  case you need it. Option is an optionated type in the shape of an HTML
  <option> with an additional optional react-select-specific keyval. It can be
  used to help cast types depending on whether you are using single or multi
  value options.
*/

export default SelectMenuCreatable;